/**
 * The mode contract and the mode registry (SPEC §2 `modes/types.ts`, TASKS T49).
 *
 * A mode's whole job is to answer one question: *given this item, what does the learner see, and
 * what string is graded?* It is deliberately not allowed to answer any other. Modes do not grade,
 * do not persist, do not decide whether an attempt advances an interval, and never see the clock or
 * the store. Grading is `lang/validator.ts`; the interval gate is `scheduler/gate.ts`; the ordering
 * is `round/builder.ts`. A mode that started deciding any of those would put pedagogy in a place no
 * unit test can reach.
 *
 * The presentation is **data**, for the same reason a card is (`ui/shell.ts`): a mode that built its
 * own DOM would be a mode that could quietly place a control above the thumb line, spell a blank
 * with the answer's own letter count, or forget the English gloss. As data, every one of those is a
 * plain assertion in a test with no browser in it.
 *
 * ## Why extras exist
 *
 * `ItemRecord` carries what the *learner's history* needs — `es`, `en`, `memory`, `failureCount` —
 * and not the presentation scaffolding a mode needs: the carrier sentence a cloze cuts a hole in,
 * the slot a conjugation fills, the rule a conversión applies. Those live in the seed corpus and are
 * looked up by id at present time rather than copied into the persisted record, so that fixing a
 * carrier sentence in `nucleo.json` reaches a learner who already has that item — a record written
 * at first launch is never rewritten from a file, because its `memory` is his and not the
 * corpus's. See DECISIONS D-112.
 */

import type { Example, Slot } from '../content/schema';
import type { ClassifyContext } from '../lang/nearmiss';
import type { Rng } from '../core/rng';
import type { ModeId } from '../state/gamestate';
import type { ItemRecord } from '../store/schema';
import { el, text } from '../ui/shell';
// One-way at runtime: every mode module imports the shapes above with `import type`, which is
// erased, so nothing here is a cycle.
import { clozePresentation } from './cloze';
import { conjugatePresentation } from './conjugate';
import { conversionPresentation } from './conversion';
import { sentencePresentation } from './sentence';
import { speechPresentation } from './speech';
import { tilesPresentation } from './tiles';

/**
 * The presentation-only fields of a seed item, which the persisted record does not carry.
 *
 * Every field is optional and every mode must cope with all of them missing: an item minted from a
 * conversation log (DoD-11) has no seed entry at all, and must still be presentable.
 */
export interface ItemExtras {
  /** The carrier sentence for a tier-2 word (cloze, tiles). */
  example?: Example;
  /** What a tier-3/6 conjugation slot asks for. */
  slot?: Slot;
  /** The `ConversionRule.id` a tier-4 item exercises. */
  rule?: string;
  /**
   * How the form is derived from something already known — the first-exposure card's last line.
   *
   * Only ever shown while teaching. A derivation on a prompt card would be the answer.
   */
  derivation?: string;
  /** What a tier-4 conversión card needs to show (T52). */
  conversion?: ConversionSpec;
}

/**
 * The conversión rule as the card presents it.
 *
 * Deliberately narrower than `ConversionRule`, which also carries its worked examples and its whole
 * hazard list: a mode that could see those could start choosing between them, and choosing what to
 * ask is the round builder's job. This is the rule *as a line of text*, plus the two facts a card
 * behaves differently on.
 */
export interface ConversionSpec {
  /** The line above the prompt: `-tion becomes -ción`, or `estar + -ando/-iendo`. */
  rule: string;
  /** The gender the ending forces, where it forces one. Null where it forces none. */
  gender: 'f' | 'm' | null;
  /**
   * Set when this item is a word the rule does **not** reach.
   *
   * The card shows the rule anyway. That is the entire mechanism: the trap has to be live or the
   * learner is not practising resisting it (BRIEF <conversion_engine>).
   */
  hazard?: { trap: string; note: string };
}

/**
 * A run of the prompt.
 *
 * A blank is a *structural* thing rather than a row of underscores in a string, so that the renderer
 * can decide what a blank looks like and a test can assert that there is exactly one of them. The
 * `length` is carried for the scaffold to use and is deliberately **not** what gets drawn: a blank
 * as wide as the answer hands over the letter count for free, which is a scaffold nobody asked for
 * and, worse, one that never marks the attempt hinted (invariant 2).
 */
export type PromptSegment =
  | { kind: 'text'; text: string }
  | { kind: 'blank'; length: number };

export interface Presentation {
  mode: ModeId;
  /** One short line: what to do. Never encouragement. */
  instruction: string;
  /** The prompt itself. Spanish for a cloze; English where the mode asks for a translation. */
  segments: readonly PromptSegment[];
  /** The English that disambiguates the prompt. Null where the prompt is already English. */
  gloss: string | null;
  /** The string grading compares the answer against. */
  expected: string;
  /**
   * What the classifier needs to name the error (T56).
   *
   * Without it, "hablas" for "hablo" is classified as a spelling slip, because nothing in the two
   * strings says they are the same verb in different persons. The mode is the only place that knows
   * — a conjugation card was built from a slot — so the mode is what supplies it. Absent where there
   * is nothing to say, which is most cards.
   */
  context?: ClassifyContext;
  /**
   * The help this card can offer, if asked.
   *
   * Present here does **not** mean shown: offering a scaffold is not using one (`RoundDraw`
   * `scaffoldOffered`). The reveal control and the `hinted` marking it implies arrive in T54; until
   * then this string is computed and never rendered, which is the safe order — a reveal that shipped
   * before the hint flag would silently advance intervals on helped answers (invariant 2).
   */
  scaffold: string | null;
  /**
   * How the answer is given.
   *
   * `text` is the default and the only one that needs no extra state from the card. `tiles` means
   * the answer is assembled from `tiles`, and the run card owns the placement — a mode is a pure
   * description of what is asked, and a mode that held the half-built answer would be holding run
   * state that a reload has to be able to discard (DoD-5: the in-flight item is the one thing a
   * reload is *supposed* to lose).
   */
  input: AnswerInput;
  /** The tile faces, in laid-out order. Present exactly when `input` is `tiles`. */
  tiles?: readonly string[];
  /** Label on the answer field. */
  fieldLabel: string;
  /** Announced to the live region when the card mounts. */
  announce: string;
}

/**
 * How the answer is given.
 *
 * `speech` is a *production* input like the others, not a special case: the transcript is graded by
 * the same validator against the same expected string, and advances intervals like any other
 * production attempt (invariant 1). What differs is only where the string comes from.
 */
export type AnswerInput = 'text' | 'tiles' | 'speech';

export interface ModeInput {
  item: ItemRecord;
  extras: ItemExtras;
  /**
   * A stream for modes whose card has a random element — the tile shuffle, and the hazard
   * interleaving in T52.
   *
   * Forked per draw by the composition root, never created here, so the same seed lays the same
   * tiles out twice and a run stays replayable (DoD-14). Optional because most modes have nothing
   * to randomise, and a mode that has one must still behave without it.
   */
  rng?: Rng;
}

/** What a blank is spoken as, and what stands in for it in any plain-text rendering. */
export const BLANK_WORD = 'blank';

/** Fixed width, and never the answer's width. See `PromptSegment`. */
const BLANK_GLYPH = '______';

/**
 * Flatten a prompt to a string, with each blank rendered as `filler`.
 *
 * Used for the live-region announcement, where "blank" is a word a screen reader can say, and by
 * tests. Never used to build the visible prompt: that is `promptSegments`, which keeps the blank as
 * an element so it can be styled and counted.
 */
export function segmentsToText(
  segments: readonly PromptSegment[],
  filler: string = BLANK_GLYPH,
): string {
  return segments
    .map((segment) => (segment.kind === 'text' ? segment.text : filler))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Build the visible prompt. The blank is an element, not a run of underscores in a string. */
export function promptSegments(
  segments: readonly PromptSegment[],
  testId: string,
  doc: Document = document,
): HTMLElement {
  const node = el('p', 'prompt', doc);
  node.dataset['testid'] = testId;
  // A blank means the text around it is the Spanish being worked on; with no blank the prompt is the
  // English being translated. Marking English as Spanish would have a screen reader read it in the
  // wrong language, which on the one card that is meant to be *heard* is worse than saying nothing.
  if (segments.some((segment) => segment.kind === 'blank')) node.lang = 'es';

  for (const segment of segments) {
    if (segment.kind === 'text') {
      node.append(doc.createTextNode(segment.text));
      continue;
    }
    const blank = text('span', 'blank', BLANK_GLYPH, `${testId}-blank`, doc);
    // The gap is a missing word, not decoration: without this a screen reader reads six underscores
    // or, worse, nothing at all, and the card becomes unanswerable rather than merely ugly.
    blank.setAttribute('role', 'img');
    blank.setAttribute('aria-label', BLANK_WORD);
    node.append(blank);
  }

  return node;
}

// ---------------------------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------------------------

/**
 * The presentation every mode falls back to until its own module lands (T50–T53, T61).
 *
 * English shown, Spanish asked for. It is a real, playable production task rather than a
 * placeholder — a half-built mode must never mean a draw the learner cannot answer, because the
 * round builder has already counted that draw and the item is already due.
 */
export function genericPresentation(input: ModeInput, mode: ModeId): Presentation {
  const { item } = input;
  return {
    mode,
    instruction: 'Say it in Spanish',
    segments: [{ kind: 'text', text: item.en }],
    gloss: null,
    expected: item.es,
    scaffold: null,
    input: 'text',
    fieldLabel: 'Your answer',
    announce: `${item.en}. Type it in Spanish.`,
  };
}

/**
 * Present one draw.
 *
 * A total function over `ModeId`: every mode returns something answerable, whether or not its module
 * exists yet. Registered here rather than looked up through a mutable map so that adding a mode is a
 * type error until this switch handles it.
 */
export function present(mode: ModeId, input: ModeInput): Presentation {
  switch (mode) {
    case 'cloze':
      return clozePresentation(input);
    case 'tiles':
      return tilesPresentation(input);
    case 'conjugate':
      return conjugatePresentation(input);
    case 'conversion':
      return conversionPresentation(input);
    case 'sentence':
      return sentencePresentation(input);
    case 'speech':
      return speechPresentation(input);
    default:
      return genericPresentation(input, mode);
  }
}
